import React, { useMemo } from "react";
import {
  Drawer,
  List,
  Badge,
  Typography,
  Button,
  Space,
  Tag,
  Tooltip,
  Empty,
  Avatar,
  Dropdown,
  MenuProps,
  Spin,
  Alert,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseOutlined,
  MoreOutlined,
  EyeOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useTheme } from "../context/ThemeContext";
import {
  useHRMSNotifications,
  HRMSNotification,
} from "../context/HRMSNotificationContext";
import { useNavigate } from "react-router-dom";

const { Text, Paragraph } = Typography;

interface HRMSNotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const HRMSNotificationPanel: React.FC<HRMSNotificationPanelProps> = ({
  visible,
  onClose,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetchNotifications,
  } = useHRMSNotifications();

  const isDarkMode = theme.algorithm?.[0]?.toString().includes("dark");

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#1f1f1f" : "#ffffff",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      itemHoverBackground: isDarkMode ? "#262626" : "#f5f5f5",
      unreadBackground: isDarkMode ? "#262626" : "#f0f6ff",
    }),
    [isDarkMode]
  );

  const getNotificationIcon = (notification: HRMSNotification) => {
    const iconProps = { style: { fontSize: "16px" } };

    // HRMS entity-specific icons
    switch (notification.entityType) {
      case "reimbursement":
        return (
          <DollarOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#52c41a" }}
          />
        );
      case "leave_request":
        return (
          <CalendarOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#1890ff" }}
          />
        );
      case "attendance":
        return (
          <TeamOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#722ed1" }}
          />
        );
      default:
        break;
    }

    // Fallback to type-based icons
    switch (notification.type) {
      case "success":
        return (
          <CheckCircleOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#52c41a" }}
          />
        );
      case "warning":
        return (
          <WarningOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#faad14" }}
          />
        );
      case "error":
        return (
          <ExclamationCircleOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#ff4d4f" }}
          />
        );
      default:
        return (
          <InfoCircleOutlined
            {...iconProps}
            style={{ ...iconProps.style, color: "#1890ff" }}
          />
        );
    }
  };

  const getPriorityColor = (priority?: HRMSNotification["priority"]) => {
    switch (priority) {
      case "critical":
        return "#ff4d4f";
      case "high":
        return "#fa8c16";
      case "medium":
        return "#1890ff";
      case "low":
        return "#52c41a";
      default:
        return "#d9d9d9";
    }
  };

  const getCategoryColor = (category?: HRMSNotification["category"]) => {
    switch (category) {
      case "security":
        return "red";
      case "system":
        return "blue";
      case "user":
        return "green";
      case "update":
        return "orange";
      case "approval":
        return "purple";
      case "review":
        return "cyan";
      default:
        return "default";
    }
  };

  const getEntityLabel = (entityType?: string) => {
    switch (entityType) {
      case "reimbursement":
        return "Reimbursement";
      case "leave_request":
        return "Leave";
      case "attendance":
        return "Attendance";
      case "employee":
        return "Employee";
      case "department":
        return "Department";
      case "designation":
        return "Designation";
      default:
        return undefined;
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return timestamp.toLocaleDateString();
    }
  };

  const handleNotificationClick = (notification: HRMSNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // HRMS-specific routing
    if (notification.entityType === "reimbursement" && notification.entityId) {
      const actionText = notification.actionText?.toLowerCase() || "";
      let from = "notifications";
      if (actionText.includes("review")) from = "pending-review";
      else if (actionText.includes("recommend")) from = "pending-recommendation";
      else if (actionText.includes("approv")) from = "pending-approval";
      navigate(`/hrms/reimbursements/${notification.entityId}?from=${from}`);
      onClose();
      return;
    }

    if (notification.entityType === "leave_request" && notification.entityId) {
      navigate(`/hrms/leave-requests/${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.entityType === "attendance") {
      navigate("/hrms/attendance");
      onClose();
      return;
    }

    if (notification.entityType === "employee" && notification.entityId) {
      navigate(`/hrms/employees/${notification.entityId}`);
      onClose();
      return;
    }

    // Fallback to actionUrl
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const getNotificationMenuItems = (
    notification: HRMSNotification
  ): MenuProps["items"] => [
    ...(notification.read
      ? [
          {
            key: "unread",
            label: "Mark as unread",
            icon: <EyeOutlined />,
            onClick: (e: any) => {
              e?.domEvent?.stopPropagation();
              markAsUnread(notification.id);
            },
          },
        ]
      : [
          {
            key: "read",
            label: "Mark as read",
            icon: <CheckOutlined />,
            onClick: (e: any) => {
              e?.domEvent?.stopPropagation();
              markAsRead(notification.id);
            },
          },
        ]),
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e: any) => {
        e?.domEvent?.stopPropagation();
        deleteNotification(notification.id);
      },
    },
  ];

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [notifications]);

  const getActionButtonLabel = (notification: HRMSNotification) => {
    if (notification.actionText) return notification.actionText;
    if (notification.category === "approval") return "Review & Approve";
    if (notification.entityType === "reimbursement") return "View Reimbursement";
    if (notification.entityType === "leave_request") return "View Request";
    return "View Details";
  };

  const getActionButtonIcon = (notification: HRMSNotification) => {
    switch (notification.entityType) {
      case "reimbursement":
        return <DollarOutlined />;
      case "leave_request":
        return <CalendarOutlined />;
      case "attendance":
        return <TeamOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const headerActions = (
    <Space>
      <Tooltip title="Refresh notifications">
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={refetchNotifications}
          loading={isLoading}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Mark all as read">
        <Button
          type="text"
          icon={<CheckOutlined />}
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          size="small"
        />
      </Tooltip>
    </Space>
  );

  return (
    <Drawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space align="center">
            <BellOutlined />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Badge
                count={unreadCount}
                size="small"
                style={{ backgroundColor: "#ff4d4f" }}
              />
            )}
          </Space>
          {headerActions}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={420}
      className="hrms-notification-panel"
      styles={{
        body: {
          padding: 0,
          background: themeTokens.background,
        },
        header: {
          borderBottom: `1px solid ${themeTokens.borderColor}`,
          background: themeTokens.background,
        },
      }}
      closeIcon={<CloseOutlined />}
    >
      {error && (
        <Alert
          message="Error loading notifications"
          description={error}
          type="error"
          showIcon
          style={{ margin: "16px" }}
          action={
            <Button size="small" onClick={refetchNotifications}>
              Retry
            </Button>
          }
        />
      )}

      {isLoading && (
        <div style={{ textAlign: "center", padding: "24px" }}>
          <Spin size="large" />
        </div>
      )}

      {!isLoading && !error && (
        <>
          {notifications.length > 0 && (
            <div
              style={{
                padding: "16px",
                borderBottom: `1px solid ${themeTokens.borderColor}`,
              }}
            >
              <Space
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <Text type="secondary">
                  {notifications.length} notification
                  {notifications.length !== 1 ? "s" : ""}
                  {unreadCount > 0 && ` \u2022 ${unreadCount} unread`}
                </Text>
                {notifications.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={clearAllNotifications}
                    style={{
                      padding: 0,
                      color: themeTokens.textColorSecondary,
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </Space>
            </div>
          )}

          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: themeTokens.textColorSecondary }}>
                  No notifications
                </span>
              }
              style={{
                marginTop: "60px",
                color: themeTokens.textColorSecondary,
              }}
            />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={sortedNotifications}
              style={{ height: "calc(100vh - 120px)", overflow: "auto" }}
              renderItem={(notification) => (
                <List.Item
                  key={notification.id}
                  className={`notification-priority-${notification.priority || "medium"}`}
                  style={{
                    padding: "16px",
                    borderBottom: `1px solid ${themeTokens.borderColor}`,
                    backgroundColor: notification.read
                      ? "transparent"
                      : themeTokens.unreadBackground,
                    cursor:
                      notification.actionUrl || notification.entityId
                        ? "pointer"
                        : "default",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (notification.actionUrl || notification.entityId) {
                      e.currentTarget.style.backgroundColor =
                        themeTokens.itemHoverBackground;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notification.read
                      ? "transparent"
                      : themeTokens.unreadBackground;
                  }}
                  onClick={() => handleNotificationClick(notification)}
                  actions={[
                    <Dropdown
                      menu={{
                        items: getNotificationMenuItems(notification),
                      }}
                      trigger={["click"]}
                      key="more"
                    >
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={getNotificationIcon(notification)}
                        style={{ backgroundColor: "transparent" }}
                      />
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <Text
                          strong={!notification.read}
                          style={{
                            color: themeTokens.textColor,
                            fontSize: "14px",
                            flex: 1,
                          }}
                        >
                          {notification.title}
                        </Text>
                        {!notification.read && (
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: "#1890ff",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Paragraph
                          style={{
                            margin: 0,
                            marginBottom: "8px",
                            color: themeTokens.textColorSecondary,
                            fontSize: "13px",
                            lineHeight: "1.4",
                          }}
                          ellipsis={{ rows: 2, expandable: false }}
                        >
                          {notification.message}
                        </Paragraph>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          <Space size="small">
                            {getEntityLabel(notification.entityType) && (
                              <Tag
                                color="geekblue"
                                style={{
                                  fontSize: "11px",
                                  padding: "0 6px",
                                  margin: 0,
                                }}
                              >
                                {getEntityLabel(notification.entityType)}
                              </Tag>
                            )}
                            {notification.category && (
                              <Tag
                                color={getCategoryColor(
                                  notification.category
                                )}
                                style={{
                                  fontSize: "11px",
                                  padding: "0 6px",
                                  margin: 0,
                                }}
                              >
                                {notification.category}
                              </Tag>
                            )}
                            {notification.priority && (
                              <Tag
                                style={{
                                  fontSize: "11px",
                                  padding: "0 6px",
                                  margin: 0,
                                  backgroundColor: getPriorityColor(
                                    notification.priority
                                  ),
                                  color: "#fff",
                                  border: "none",
                                }}
                              >
                                {notification.priority}
                              </Tag>
                            )}
                          </Space>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "11px",
                              color: themeTokens.textColorSecondary,
                            }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Text>
                        </div>
                        {(notification.actionText ||
                          notification.category === "approval" ||
                          notification.entityId) && (
                          <Button
                            type="link"
                            size="small"
                            icon={getActionButtonIcon(notification)}
                            style={{
                              padding: "4px 0",
                              height: "auto",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {getActionButtonLabel(notification)} →
                          </Button>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default React.memo(HRMSNotificationPanel);
